import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {navigator} from 'lit-element-router';
import {msg} from '@lit/localize';
import store from '../store';

import {login, silentLogin, logout} from '../slices/userSlice';
import account from '../../icons/account';

@customElement('login-page')
export class LoginPage extends connect(store)(navigator(LitElement)) {

	@property()
	email = '';

	@property()
	password = '';

	static styles = [
		css`
			:host {
				padding-top: 100px;
				padding-bottom: 100px;
				display: flex;
				flex-direction: column;
				gap: var(--large-space);
			}
			.loginPage {
				background-color: #ffffff;
				display: flex;
				flex-direction: row;
				overflow: hidden;
				gap: 20px;
				width: 100%;
				padding: 50px 20px 0 20px;
				max-height: initial;
				box-sizing: border-box;
				align-items: start;
			}
			.login {
				padding-top: 40px;
				display: flex;
				flex-direction: column;
				gap: var(--large-space);
			}

			.title {
				font: var(--font-downloads-title);
				color: var(--color-white);
			}
			.subtitle {
				padding: var(--padding) 0;
				font: var(--font-downloads-title);
				color: var(--color-white);
			}
			.description {
				font: var(--font-downloads-description);
				color: var(--color-white);
			}
			.login-form {
				display: flex;
				flex-direction: column;
			}
			input {
				border: 0 none;
				border-bottom: 1px solid var(--color-widget-border);
				padding: var(--small-padding) var(--padding);
				margin-bottom: var(--padding);
				font: var(--font-light);
			}
			input::placeholder {
				font: var(--font-light);
				color: var(--color-widget-border);
			}
			label {
				display: none;
			}
			button {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				text-align: left;
				font: var(--font-widget-small-title);
				padding: var(--very-small-padding) var(--padding);
				color:  var(--color-black);
				border: 1px solid var(--color-black);
				background-color: var(--color-white);
				cursor: pointer;
			}
			button:disabled {
				background-color: var(--color-grey5);
				color: var(--color-grey5);
			}

			.icon {
				display: inline-block;
				height: 16px;
				width: 16px;
				margin-right: 10px;
				color: var(--color-white);
			}
			.downloadButton .icon {
				color: var(--color-black);
			}
			.icon.state {
				color: var(--color-warn);
			}
			.icon.state.satisfied {
				color: var(--color-ok);
			}

			.icon svg {
				width: 100%;
				height: auto;
			}

			.links {
				padding-top: 40px;
				flex-basis: 80%;
			}

			.linklist {
				display: flex;
				flex-direction: column;
			}
			.linklist a {
				font: var(--font-light);
				color: var(--color-white);
				text-decoration: none;
				padding: var(--very-small-padding) 0;
				border-bottom: 1px solid var(--color-widget-border);
			}
			.linklist a:first-child {
				border-top: 1px solid var(--color-widget-border);
			}
		`

	];

	constructor() {
		super();
		if (this.getCookie('slxcfgsession')) {
			store.dispatch(silentLogin({}));
		}
		store.subscribe(() => this.requestUpdate());
	}
	connectedCallback() {
		super.connectedCallback();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
	}
	stateChanged(state) {
		this.state = state;
		this.userProfile = state.userProfile.data;
		this.info = state.configuration.data.projects[0].project.info;
		this.status = state.status;
	}

	onLogin() {
		store.dispatch(login({
			userName: this.email,
			password: this.password
		}));
	}
	onLogout() {
		store.dispatch(logout());
	}

	handleInput(e) {
		this[e.target.id] = e.target.value;
		if(e.key === 'Enter') {
			this.onLogin();
		}
		this.requestUpdate();
	}

	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		} else {
			return false;
		}
	}

	render() {
		const loginEnabled = this.email && this.email !== '' && this.password && this.password !== '';

		return html`
			<div class="loginPage">
				<div class="login">
					<div class="title">${msg('Login')}</div>
					<div class="login-form ${classMap({loggedIn: this.userProfile.loggedIn, loginFailed: this.userProfile.loginFailed})}">
							${when(this.userProfile.loggedIn, () => html`
								<div>${msg('Logged in:')} ${this.userProfile.userName}<br><br></div>
								<button class="submit" @click=${this.onLogout}>${msg('logout')}</button>
							`, () => html`
								<div class="description">${msg('Please enter your credentials to login.')}</div>
								<label class="field-name" for="email">${msg('Username')}</label>
								<input placeholder="Username" type="text" name="email" id="email" .value=${this.email} @input=${this.handleInput} required="true">

								<label class="field-name" for="password">${msg('Password')}</label>
								<input placeholder="Password" type="password" name="password" id="password" @input=${this.handleInput} @keypress=${this.handleInput}  required="true">

								<button ?disabled="${!loginEnabled}" class="submit" @click=${this.onLogin}>
									<span>${msg('login')}</span>
									<span class="icon">${account}</span>
								</button>
							`)}
					</div>
				</div>
				<div class="links">
					${when(!this.userProfile.loggedIn, () => html`
						<div class="description">${msg('Log in to access.')}</div>
					`, () => html`
						<div class="subtitle">${msg('Downloads')}</div>
						<div class="linklist">
							<a target="_blank" href="/int/de/prices/">${msg('Pricelist')}</a>
						</div>
						<div class="subtitle">${msg('Apps with extended Features')}</div>
						<div class="linklist">
							<a href="/demo.php?lang=de&family=Avanza">${msg('Data Hub')}</a>
							<a target="_blank" href="/int/de/TheLine/sections/external/new">Line ${msg('Configurator')}</a>
							<a target="_blank" href="/int/de/Olivio/sections/external/new">Olivio ${msg('Configurator')}</a>
							<a target="_blank" href="/int/de/Aeto/sections/external/new">Aeto ${msg('Configurator')}</a>
							<a target="_blank" href="/int/de/LightProfile/sections/external/new">${msg('Light Profile')} ${msg('Configurator')}</a>
						</div>

					`)}
				</div>
			</div>
		`;
	}


}
